import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToSection } from "../../i18n";

const EASE = [0.22, 1, 0.36, 1];
const OTA_RATES = [0.15, 0.17, 0.03];
const eur = (n) => `€${Math.round(n).toLocaleString("it-IT")}`;

function Slider({ label, value, min, max, step, onChange, testid }) {
  return (
    <label className="grid gap-2">
      <span className="flex items-center justify-between text-xs font-extrabold uppercase tracking-[0.15em] text-navy">
        {label}
        <span className="rounded-full bg-navy px-3 py-1 font-display text-sm font-semibold normal-case tracking-normal text-white">{eur(value)}</span>
      </span>
      <input
        data-testid={testid}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-navy/15 accent-sky"
      />
    </label>
  );
}

function ResultCard({ title, rows, highlight, dark }) {
  return (
    <div className={`rounded-[1.5rem] p-6 ring-1 ${dark ? "bg-navy text-white ring-navy" : "bg-white ring-navy/10"}`}>
      <p className={`text-[11px] font-extrabold uppercase tracking-[0.2em] ${dark ? "text-sky" : "text-mute"}`}>{title}</p>
      <ul className="mt-4 space-y-2.5 text-sm">
        {rows.map(([label, value, strong]) => (
          <li key={label} className={`flex items-center justify-between gap-3 ${strong ? "border-t pt-2.5 font-extrabold " + (dark ? "border-white/15" : "border-navy/10") : "font-semibold"}`}>
            <span className={dark && !strong ? "text-white/65" : !strong ? "text-mute" : ""}>{label}</span>
            <span className={strong ? (dark ? "text-sky" : "text-navy") : ""}>{value}</span>
          </li>
        ))}
      </ul>
      {highlight && <p className={`mt-4 rounded-xl px-4 py-2.5 text-center font-display text-xl font-semibold ${dark ? "bg-sky/15 text-sky" : "bg-sand text-navy"}`}>{highlight}</p>}
    </div>
  );
}

export default function Calculator({ content }) {
  const [stay, setStay] = useState(1200);
  const [cleaning, setCleaning] = useState(80);
  const [channel, setChannel] = useState(0);
  const [taxRate, setTaxRate] = useState(21);
  const [mgmt, setMgmt] = useState(20);

  const r = useMemo(() => {
    const ota = stay * OTA_RATES[channel];
    const mgmtFee = (stay * mgmt) / 100;
    const tradBase = stay - ota;
    const tradTax = (tradBase * taxRate) / 100;
    const tradNet = stay - ota - mgmtFee - tradTax;
    const ogBase = stay - ota - mgmtFee;
    const ogTax = (ogBase * taxRate) / 100;
    const ogNet = stay - ota - ogTax;
    const adr = stay / 4;
    const season = adr * 0.7 * 150;
    return { ota, mgmtFee, tradBase, tradTax, tradNet, ogBase, ogTax, ogNet, adr, season };
  }, [stay, cleaning, channel, taxRate, mgmt]);

  const diff = r.ogNet - r.tradNet;
  const c = content;

  return (
    <div data-testid="services-calculator" className="rounded-[2rem] bg-white p-7 ring-1 ring-navy/10 sm:p-10">
      <h3 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-3xl">{c.title}</h3>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="space-y-6">
          <Slider label={c.stay} value={stay} min={300} max={5000} step={50} onChange={setStay} testid="calc-stay-slider" />
          <Slider label={c.cleaning} value={cleaning} min={0} max={300} step={10} onChange={setCleaning} testid="calc-cleaning-slider" />
          <div>
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{c.channel}</span>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {c.channels.map((ch, i) => (
                <button key={ch} data-testid={`calc-channel-${i}`} onClick={() => setChannel(i)} className={`rounded-full px-4 py-2 text-xs font-bold transition-colors duration-300 ${channel === i ? "bg-navy text-white" : "bg-sand text-mute ring-1 ring-navy/10 hover:text-navy"}`}>
                  {ch}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{c.tax}</span>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {[21, 26].map((v) => (
                <button key={v} data-testid={`calc-tax-${v}`} onClick={() => setTaxRate(v)} className={`rounded-full px-4 py-2 text-xs font-bold transition-colors duration-300 ${taxRate === v ? "bg-navy text-white" : "bg-sand text-mute ring-1 ring-navy/10 hover:text-navy"}`}>
                  {v}%
                </button>
              ))}
              <span className="flex items-center gap-1.5 rounded-full bg-sand px-4 py-2 text-xs font-bold text-navy ring-1 ring-navy/10">
                <input data-testid="calc-tax-custom" type="number" min="0" max="45" value={taxRate} onChange={(e) => setTaxRate(Math.min(45, Math.max(0, Number(e.target.value) || 0)))} className="w-10 bg-transparent focus:outline-none" />
                %
              </span>
            </div>
          </div>
          <Slider label={c.mgmt} value={mgmt} min={10} max={25} step={1} onChange={setMgmt} testid="calc-mgmt-slider" />
        </div>

        <div className="grid content-start gap-4">
          <ResultCard
            title={c.traditional}
            rows={[
              [c.rows.guest, eur(stay + cleaning)],
              [c.rows.ota, `− ${eur(r.ota)}`],
              [c.rows.cleaning, `− ${eur(cleaning)}`],
              [c.rows.mgmt, `− ${eur(r.mgmtFee)}`],
              [c.rows.base, eur(r.tradBase)],
              [c.rows.tax, `− ${eur(r.tradTax)}`],
              [c.rows.net, eur(r.tradNet), true],
            ]}
          />
          <ResultCard
            dark
            title={c.ogstay}
            rows={[
              [c.rows.guest, eur(stay + cleaning)],
              [c.rows.ota, `− ${eur(r.ota)}`],
              [c.rows.cleaning, eur(cleaning)],
              [c.rows.mgmt, eur(r.mgmtFee)],
              [c.rows.base, eur(r.ogBase)],
              [c.rows.tax, `− ${eur(r.ogTax)}`],
              [c.rows.net, eur(r.ogNet), true],
            ]}
            highlight={diff > 0 ? `+ ${eur(diff)}` : undefined}
          />
        </div>
      </div>

      <p className="mt-6 text-xs italic leading-relaxed text-mute">{c.disclaimer}</p>

      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="mt-8 rounded-[1.5rem] bg-sand p-6 ring-1 ring-navy/5 sm:p-8">
        <p className="font-display text-lg italic text-navy sm:text-xl">{c.seasonIntro}</p>
        <p className="mt-2 text-xs font-extrabold uppercase tracking-[0.2em] text-mute">{c.seasonFormula}</p>
        <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-3 text-sm font-semibold text-ink">
          <span>
            <span className="block text-[11px] uppercase tracking-wider text-mute">{c.seasonLabels.adr}</span>
            {eur(r.adr)}
          </span>
          <span aria-hidden className="pb-0.5 text-mute">×</span>
          <span>
            <span className="block text-[11px] uppercase tracking-wider text-mute">{c.seasonLabels.occ}</span>
            70%
          </span>
          <span aria-hidden className="pb-0.5 text-mute">×</span>
          <span>
            <span className="block text-[11px] uppercase tracking-wider text-mute">{c.seasonLabels.days}</span>
            150
          </span>
          <span aria-hidden className="pb-0.5 text-mute">=</span>
          <span className="font-display text-2xl font-semibold text-navy">{eur(r.season)}</span>
        </div>
        <p className="mt-3 text-sm font-semibold text-med">{c.seasonNote}</p>
      </motion.div>

      <button data-testid="calc-cta" onClick={() => scrollToSection("#valutazione")} className="btn-primary mt-8 w-full sm:w-auto">
        {c.cta}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
